const MAIN_DOMAIN = 'mof.gov.tl';

/**
 * Subdomains served by this app, and how each one finds its directorate in
 * Strapi. Directorate slugs are generated from names editors can change, so a
 * subdomain is matched against the directorate's name and slug by keywords
 * (accents ignored) rather than pinned to one exact slug.
 *
 * `live: true` once the subdomain has DNS, a Netlify domain alias and a valid certificate.
 * Only live subdomains are linked from the main portal; set it as each one goes live.
 */
export const SUBDOMAINS = {
    budget: { title: 'Budget Planning', keywords: ['orsamentu', 'orcamento', 'budget'], live: true },
    treasury: { title: 'Treasury', keywords: ['tezouru', 'tesouro', 'treasury'] },
    assets: { title: 'State Asset Management', keywords: ['patrimoniu', 'patrimonio', 'asset'] },
    corporate: { title: 'Corporate Services', keywords: ['korporativu', 'corporativ', 'corporate'] },
    // One office covers both PFM reform and capacity building.
    'pfm-training': {
        title: 'PFM Reform and Capacity Building',
        keywords: ['reforma', 'reform', 'capacitacao', 'kapasitasaun', 'capacity'],
    },
    petroleumfund: { title: 'Petroleum Fund', keywords: ['minarai', 'petrolifero', 'petroleum'] },
    communications: { title: 'Communications', keywords: ['komunikasaun', 'comunicacao', 'communication'] },
    legal: { title: 'Legal Affairs', keywords: ['juridiku', 'juridico', 'legal'] },
    audit: { title: 'Inspector General', keywords: ['inspesaun', 'inspecao', 'inspector'] },
};

/**
 * Subdomains that show one of the portal's own pages instead of a directorate.
 * `path` is that page on www, used when the subdomain is not live or not reachable.
 */
export const APP_SUBDOMAINS = {
    lia: { title: 'LIA AI', path: '/lia', live: true },
};

export const isAppSubdomain = (subdomain) => Boolean(APP_SUBDOMAINS[subdomain]);

/** `budget` on budget.mof.gov.tl, or `?subdomain=budget` for local testing. */
export function getSubdomain() {
    const subParam = new URLSearchParams(window.location.search).get('subdomain');
    if (subParam) return subParam;

    const { hostname } = window.location;
    if (hostname.endsWith(`.${MAIN_DOMAIN}`)) {
        const sub = hostname.slice(0, -(MAIN_DOMAIN.length + 1));
        if (sub && sub !== 'www') return sub;
    }
    return null;
}

/** Absolute URL of `path` on the main portal (www.mof.gov.tl) when on a subdomain. */
export function getMainPortalUrl(subdomain, path = '/') {
    if (!subdomain) return path;
    const { hostname, host, origin, protocol } = window.location;
    if (!hostname.endsWith(`.${MAIN_DOMAIN}`)) return `${origin}${path}`;
    // The portal is served on www; the bare domain only redirects there (or may not be set up).
    return `${protocol}//${host.replace(`${subdomain}.`, 'www.')}${path}`;
}

export const getSubdomainTitle = (subdomain) =>
    SUBDOMAINS[subdomain]?.title || APP_SUBDOMAINS[subdomain]?.title || subdomain.toUpperCase();

/**
 * Strapi query filters selecting the menus of one site. Main Menus without a Site belong to
 * www.mof.gov.tl; Main Menus linked to a Site belong to that subdomain. Sub-menus follow their
 * main menu. Pass no subdomain for the main portal.
 */
export function menuFilters(subdomain) {
    const site = subdomain
        ? `[site][subdomain][$eq]=${encodeURIComponent(subdomain)}`
        : '[site][id][$null]=true';
    return { main: `filters${site}`, sub: `filters[main_menu]${site}` };
}

/**
 * The Site entry for a subdomain from the CMS (title and directorate slug), or null if the
 * subdomain has no Site yet. Shared by the navigation and the subdomain pages.
 */
const siteRequests = new Map();
export function fetchSite(apiUrl, subdomain) {
    if (!siteRequests.has(subdomain)) {
        const query = `filters[subdomain][$eq]=${encodeURIComponent(subdomain)}`
            + '&populate[directorate][fields][0]=slug&populate[directorate][fields][1]=directorateName';
        siteRequests.set(subdomain, fetch(`${apiUrl}/sites?${query}`, { credentials: 'omit' })
            .then((response) => (response.ok ? response.json() : { data: [] }))
            .then((result) => {
                const attributes = result?.data?.[0]?.attributes;
                if (!attributes) return null;
                const directorate = attributes.directorate?.data?.attributes || {};
                return {
                    subdomain,
                    title: attributes.title || getSubdomainTitle(subdomain),
                    isLive: attributes.isLive === true,
                    directorateSlug: directorate.slug || null,
                    directorateName: directorate.directorateName || '',
                };
            })
            .catch(() => {
                siteRequests.delete(subdomain);
                return null;
            }));
    }
    return siteRequests.get(subdomain);
}

/** Name of a directorate entry, whichever field the CMS filled in. */
export const directorateName = (fields = {}) =>
    fields.directorateName || fields.title || fields.name || '';

const normalize = (value) =>
    (value || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');

/** https://<subdomain>.mof.gov.tl/ */
export const subdomainUrl = (subdomain, path = '/') => `https://${subdomain}.${MAIN_DOMAIN}${path}`;

/**
 * The same subdomain site served from www (getSubdomain reads ?subdomain=), for visitors whose
 * network cannot reach the subdomain itself. See useSubdomainUrl. App subdomains (lia) use
 * their page on www instead.
 */
export const subdomainFallbackUrl = (subdomain, path = '/') => {
    const app = APP_SUBDOMAINS[subdomain];
    if (app) return `https://www.${MAIN_DOMAIN}${path === '/' ? app.path : path}`;
    return `https://www.${MAIN_DOMAIN}${path}${path.includes('?') ? '&' : '?'}subdomain=${encodeURIComponent(subdomain)}`;
};

/** The live subdomain that serves this directorate entry, or null. */
export function liveSubdomainForDirectorate(entry) {
    if (!entry) return null;
    const match = Object.keys(SUBDOMAINS).find(
        (key) => SUBDOMAINS[key].live && findDirectorateForSubdomain([entry], key)
    );
    return match || null;
}

/** The directorate entry a subdomain belongs to, or undefined. */
export function findDirectorateForSubdomain(entries, subdomain) {
    const config = SUBDOMAINS[subdomain];
    const keywords = (config?.keywords || [subdomain]).map(normalize);
    return entries.find((entry) => {
        const haystack = `${normalize(directorateName(entry.fields))} ${normalize(entry.fields?.slug)}`;
        return keywords.some((keyword) => haystack.includes(keyword));
    });
}
